import { combineReducers } from 'redux';
import { generateUniqueId } from 'utils/unique-id';

import { removeNulls } from 'utils/Object.utils';

type ReducerAction = {
  type?: string;
  [key: string]: any;
};

export type LedgerMeta = {
  name?: string;
  description?: string;
  createdOn?: string;
  [key: string]: unknown;
};

export type Ledger = {
  id: string;
  meta?: LedgerMeta;
  loans?: unknown[];
  assets?: unknown[];
  series?: Record<string, unknown>;
  manual?: Record<string, unknown>;
  [key: string]: unknown;
};

export type LedgerStatus = {
  isLoading?: boolean;
  error?: { code: string; [key: string]: unknown } | null;
  metaError?: { code: string; [key: string]: unknown } | null;
  [key: string]: unknown;
};

const baseLedger = (): Ledger => {
  return {
    id: generateUniqueId(),
    meta: {
      name: 'My Ledger',
      description: 'Property investment ledger',
      createdOn: new Date().toISOString(),
    },
    loans: [],
    assets: [],
    series: {
      debt: {},
      offsets: {},
      rates: {},
      intRepay: {},
      totRepay: {},
      allocations: {},
      assetIncome: {},
    },
    manual: {
      debt: {},
      offsets: {},
      rates: {},
      intRepay: {},
      totRepay: {},
    },
  };
};

export const newLedger = (props: Partial<Ledger> = {}): Ledger => {
  const base = baseLedger();
  return {
    ...base,
    ...props,
    meta: {
      ...base.meta,
      ...(props.meta || {}),
    },
  };
};

const ledgersById = (
  stateArg: Record<string, Ledger> | undefined,
  action: ReducerAction
): Record<string, Ledger> => {
  const state = stateArg || {};
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return {};
    }
    case 'LEDGERS_ADD_NEW': {
      const { ledger } = action;
      return { ...state, [ledger.id]: ledger };
    }
    case 'LEDGERS_DELETE': {
      // TODO: Propagate the deletion to remove related items
      const { ledgerId } = action;
      return removeNulls({
        ...state,
        [ledgerId]: null,
      }) as Record<string, Ledger>;
    }
    case 'LEDGERS_FINISHED_DELETING': {
      // Re-removes (or removes, if the optimistic LEDGERS_DELETE removal was
      // undone - e.g. a stale refetch/rehydration landing with a pre-delete
      // snapshot while this delete was still in flight) the deleted ledger
      // from THIS slice too - mirrors the equivalent case in
      // ledgersList.reducer.js. Without this, a ledger whose detail page was
      // previously viewed (and is therefore cached here as a FULL ledger)
      // would stay cached - stale, pre-delete - even after a confirmed
      // delete, letting Layout.react.js/LedgerSettingsForm.react.js trust it
      // as still valid and keep rendering/editing a ledger that's supposed
      // to be gone. (Separately, firebase_updateLedgerMeta in
      // ledgers.actions.js now writes only the specific meta field(s) being
      // edited via targeted per-field paths, so even a stale cached entry
      // here can no longer un-delete a ledger via a Settings-form edit -
      // this case remains defense-in-depth so the stale entry isn't trusted
      // at all.)
      const { ledgerId } = action;
      if (!ledgerId) return state;
      return removeNulls({
        ...state,
        [ledgerId]: null,
      }) as Record<string, Ledger>;
    }
    case 'LEDGER_FINISHED_LOADING': {
      const { ledger } = action;
      if (!ledger?.id) {
        return state;
      }
      return {
        ...state,
        [ledger.id]: ledger,
      };
    }
    case 'LEDGER_META_UPDATE': {
      const { ledgerId, ledgerMeta } = action;
      const ledger = state[ledgerId];
      if (!ledger) return state;
      return {
        ...state,
        [ledgerId]: {
          ...ledger,
          meta: {
            ...ledger.meta,
            ...ledgerMeta,
          },
        },
      };
    }
    case 'LEDGER_META_FINISHED_UPDATING_WITH_ERROR': {
      // Roll back the optimistic LEDGER_META_UPDATE for this ledger's cached
      // detail-page entry to the meta that was actually last persisted
      // (captured before the optimistic update - see updateLedgerMeta in
      // ledgers.actions.js). This action's id/previousMeta are nested inside
      // action.payload (unlike most other actions here, which carry a
      // top-level action.ledgerId), so it falls outside the default case
      // below and needs its own handling. Without this, a failed rename
      // could never be rolled back - it would silently keep showing the new,
      // never-saved name indefinitely.
      const { ledgerId, previousMeta } = action.payload || {};
      const ledger = state[ledgerId];
      if (!ledger || !previousMeta) return state;
      return {
        ...state,
        [ledgerId]: {
          ...ledger,
          meta: previousMeta,
        },
      };
    }
    case 'LEDGERS_FINISHED_DELETING_WITH_ERROR': {
      // Roll back an optimistic delete for this ledger's cached detail-page
      // entry too - mirrors the equivalent case in ledgersList.reducer.js.
      // This action's id is nested inside action.payload (unlike most other
      // actions here, which carry a top-level action.ledgerId), so it falls
      // outside the default case below and needs its own handling. Without
      // this, a ledger whose detail page was previously viewed (and is
      // therefore cached here) would stay permanently wiped from this slice
      // even after a successful rollback in ledgersList, incorrectly showing
      // "Ledger not found!" until a manual refresh of that page.
      //
      // Deliberately reads action.payload.fullLedger, NOT action.payload.ledger:
      // `ledger` (used by ledgersList.reducer.js's equivalent case) is the
      // LIGHTWEIGHT {id, acl, meta} shape that slice always holds - it is
      // NEVER safe to write into ledgers.byId, which must only ever contain
      // FULL ledgers (loans/assets/series/manual), populated elsewhere via
      // LEDGER_FINISHED_LOADING. `fullLedger` is instead captured from
      // ledgers.byId's own pre-delete state (see deleteLedger in
      // ledgers.actions.js), so it is only present - and only restored here -
      // when a full entry genuinely existed in this slice before the
      // optimistic delete. If none existed (this ledger's detail page was
      // never visited), fullLedger is undefined and this is a no-op: there is
      // nothing to restore, and getOrFetchLedger will correctly trigger a
      // real refetch if the user later opens the detail page. Writing the
      // lightweight `ledger` shape here instead would silently poison this
      // cache - getOrFetchLedger (used by duplicateLedger/
      // duplicateLedgerWithSegue) trusts ANY existing entry as fully loaded,
      // so a subsequent duplicate would create a copy missing all
      // loans/assets/series/manual data with no error or crash.
      const { fullLedger } = action.payload || {};
      if (!fullLedger) return state;
      return {
        ...state,
        [fullLedger.id]: fullLedger,
      };
    }
    case 'LEDGERS_FINISHED_ADDING_WITH_ERROR': {
      // Roll back the optimistic LEDGERS_ADD_NEW phantom entry, which was
      // added to THIS slice too (not just ledgersList.byId), since
      // LEDGERS_ADD_NEW is an unscoped action type picked up by both
      // reducers. Without this case, a failed add/duplicate left a permanent
      // orphan entry here: this falls through to the default case below,
      // which reads a top-level action.ledgerId that doesn't exist on this
      // action (it's nested at action.payload.ledgerId), so the lookup
      // silently misses and the phantom is never removed. Since `ledgers` is
      // in the persisted-slices whitelist, these orphans would otherwise
      // accumulate in localStorage across repeated failures.
      //
      // action.payload.ledgerId is absent (no-op) when this action type is
      // instead dispatched by getOrFetchLedger for a pre-duplicate fetch
      // failure - nothing was ever optimistically added in that case.
      const { ledgerId } = action.payload || {};
      if (!ledgerId) return state;
      return removeNulls({
        ...state,
        [ledgerId]: null,
      }) as Record<string, Ledger>;
    }
    default: {
      const { ledgerId } = action;
      const ledger = state[ledgerId];
      if (ledger) {
        return {
          ...state,
          [ledgerId]: {
            ...ledger,
          },
        };
      }
      return state;
    }
  }
};

const allLedgers = (
  stateArg: string[] | undefined,
  action: ReducerAction
): string[] => {
  const state = stateArg || [];
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return [];
    }
    case 'LEDGERS_ADD_NEW': {
      const { ledger } = action;
      return state.concat([ledger.id]);
    }
    case 'LEDGERS_DELETE': {
      const { ledgerId } = action;
      return state.filter((id) => {
        return id !== ledgerId;
      });
    }
    case 'LEDGERS_FINISHED_DELETING': {
      // Keeps allIds consistent with the byId re-remove above (see
      // ledgersById) - mirrors the equivalent case in ledgersList.reducer.js.
      const { ledgerId } = action;
      if (!ledgerId) return state;
      return state.filter((id) => {
        return id !== ledgerId;
      });
    }
    case 'LEDGER_FINISHED_LOADING': {
      const { ledger } = action;
      return [
        ...state.filter((id) => {
          return id !== ledger.id;
        }),
        ledger.id,
      ];
    }
    case 'LEDGERS_FINISHED_DELETING_WITH_ERROR': {
      // Keeps allIds consistent with the byId restore above (see ledgersById)
      // even though only byId is currently read by the detail page selector.
      // Reads action.payload.fullLedger, not action.payload.ledger - see the
      // matching case in ledgersById above for why.
      const { fullLedger } = action.payload || {};
      if (!fullLedger || state.includes(fullLedger.id)) return state;
      return state.concat([fullLedger.id]);
    }
    case 'LEDGERS_FINISHED_ADDING_WITH_ERROR': {
      // Keeps allIds consistent with the byId rollback above (see
      // ledgersById) - mirrors the equivalent case in ledgersList.reducer.js.
      const { ledgerId } = action.payload || {};
      if (!ledgerId) return state;
      return state.filter((id) => {
        return id !== ledgerId;
      });
    }
    default:
      return state;
  }
};

const statusReducer = (
  stateArg: Record<string, LedgerStatus> | undefined,
  action: ReducerAction
): Record<string, LedgerStatus> => {
  const state = stateArg || {};
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return {};
    }
    case 'LEDGER_IS_LOADING': {
      const { ledgerId } = action;
      return {
        ...state,
        [ledgerId]: {
          isLoading: true,
        },
      };
    }
    case 'LEDGER_FINISHED_LOADING': {
      const { ledger } = action;
      return {
        ...state,
        [ledger.id]: {
          isLoading: false,
          error: null,
        },
      };
    }
    case 'LEDGER_FINISHED_LOADING_WITH_ERROR': {
      const { ledgerId, error } = action;
      return {
        ...state,
        [ledgerId]: {
          error: error || { code: 'Unknown' },
          isLoading: false,
        },
      };
    }
    case 'LEDGER_META_IS_UPDATING': {
      // Clears any previously-surfaced metaError as soon as a NEW attempt
      // starts (e.g. the user edits the field again after a failure) - see
      // didUpdatingLedgerMetaErrored (ledgers.selector.js). Mirrors the
      // equivalent PORTFOLIO_META_IS_UPDATING case in portfolios.reducer.js.
      const { ledgerId } = action;
      if (!ledgerId) return state;
      const existing = state[ledgerId] || {};
      return {
        ...state,
        [ledgerId]: { ...existing, metaError: null },
      };
    }
    case 'LEDGER_META_FINISHED_UPDATING': {
      const { ledgerId } = action;
      const existing = state[ledgerId] || {};
      return {
        ...state,
        [ledgerId]: { ...existing, metaError: null },
      };
    }
    case 'LEDGER_META_FINISHED_UPDATING_WITH_ERROR': {
      // Defense in depth for the ACL-population race (see
      // utils/firebase/retryOnPermissionDenied.js) - by the time this
      // dispatches, firebase_updateLedgerMeta's bounded retry has already
      // been exhausted (or the error was never retryable to begin with), so
      // this really is a failure the user needs to see, not a transient blip
      // that will self-heal. `ledgerId` is nested inside action.payload (see
      // the equivalent ledgersById case above), so this falls outside the
      // default case below.
      const { ledgerId, error } = action.payload || {};
      if (!ledgerId) return state;
      const existing = state[ledgerId] || {};
      return {
        ...state,
        [ledgerId]: { ...existing, metaError: error || { code: 'Unknown' } },
      };
    }
    case 'LEDGER_META_ERROR_DISMISSED': {
      const { ledgerId } = action;
      if (!ledgerId || !state[ledgerId]) return state;
      return {
        ...state,
        [ledgerId]: { ...state[ledgerId], metaError: null },
      };
    }
    default: {
      return state;
    }
  }
};

const ledgersReducerCombined = combineReducers({
  byId: ledgersById,
  allIds: allLedgers,
  status: statusReducer,
});

export type LedgersState = ReturnType<typeof ledgersReducerCombined>;

const ledgersReducer = (
  state: LedgersState | undefined,
  action: ReducerAction
): LedgersState => {
  return ledgersReducerCombined(state, action as never);
};

export default ledgersReducer;
